// The node card: breadcrumb, question, options, constraints, verdicts,
// reviewer annotations, confidence, fact injection, grounding chips, peek.

import { applySuggestion, injectFact, matchOptionLabel, pick } from "../core/navigation";
import { reviewBranchesFrom, reviewNotesFor, reviewSummaries } from "../core/reviews";
import { pathTo, state, type DecisionNode } from "../core/state";
import type { RenderNodeOpts } from "../core/bus";
import { truncate } from "../lib/text";
import { $, bar, el, renderThinking } from "./dom";

export const VERDICT_CLS: Record<string, string> = { sound: "v-sound", weak: "v-weak", risky: "v-risky" };

function renderBreadcrumb(node: DecisionNode): void {
  const bc = $("#breadcrumb");
  bc.innerHTML = "";
  bc.appendChild(el("span", "bc", "root"));
  for (const nd of pathTo(node).slice(0, -1)) {   // ancestors only
    if (nd.chosenIndex == null) continue;
    bc.appendChild(el("span", "sep", "›"));
    const label = nd.options[nd.chosenIndex]?.label || "";
    bc.appendChild(el("span", nd.depth === 0 ? "bc branch" : "bc", truncate(label, 34)));
  }
}

export function renderNode(node: DecisionNode, opts: RenderNodeOpts = {}): void {
  const main = $("#nodeCard");
  renderBreadcrumb(node);

  if (opts.thinking) {
    renderThinking(main, "navigator is composing the next decision…", 3);
    if (opts.keepQuestion) main.insertBefore(el("div", "question", node.question), main.querySelector(".skeleton"));
    return;
  }

  main.innerHTML = "";

  // meta row: branch tag + speed badge
  const instant = node.lastLatency === 0;   // prefetched/revisited → 0 ms perceived
  const meta = el("div", "node-meta");
  if (node.depth > 0) meta.appendChild(el("span", "branch-tag", node.branch || "design"));
  const badge = el("span", "speed-badge" + (instant ? " instant" : ""));
  badge.appendChild(document.createTextNode(
    instant ? "⚡ prefetched · 0 ms"
      : node.lastLatency != null ? `generated · ${node.lastLatency} ms` : "generated"));
  meta.appendChild(badge);
  if (node.verdict) {
    const v = el("span", "verdict-chip " + (VERDICT_CLS[node.verdict.verdict] || "v-sound"));
    v.textContent = "review: " + node.verdict.verdict;
    v.title = node.verdict.note || "";
    meta.appendChild(v);
  }
  if (node.depth > 0) {
    meta.appendChild(el("span", "stakes-chip stakes-" + (node.stakes || "med"), (node.stakes || "med") + " stakes"));
  }
  main.appendChild(meta);

  // question
  main.appendChild(el("div", "question", node.question));
  const lowStakes = node.depth > 0 && node.stakes === "low";
  main.appendChild(el("div", "q-hint",
    node.depth === 0 ? "Pick a branch to descend into — press its number."
      : lowStakes ? "Low-stakes — press ↹ Tab to accept the recommended default and keep moving."
        : "Press a number to decide, or ↹ Tab to accept the recommended default. The next node is already waiting."));

  // cross-branch constraints pinned by the background compiler (§11)
  if (state.constraints.length) {
    const cw = el("div", "constraints");
    state.constraints.forEach((c) => {
      const chip = el("div", "cchip");
      chip.appendChild(el("span", "ctag", "⛓ cross-branch"));
      chip.appendChild(document.createTextNode(c.text));
      if (c.branches.length) chip.title = "between: " + c.branches.join(" ↔ ");
      cw.appendChild(chip);
    });
    main.appendChild(cw);
  }

  // options
  const rec = node.recommend ?? 0;
  const optsEl = el("div", "options");
  node.options.forEach((o, i) => {
    const row = el("div", "option" + (node.chosenIndex === i ? " sel" : "") + (i === rec ? " rec" : ""));
    row.style.animationDelay = (i * 45) + "ms";
    row.dataset.i = String(i);
    row.appendChild(el("span", "num", String(i + 1)));
    const txt = el("div", "otxt");
    const lbl = el("div", "olabel");
    lbl.appendChild(document.createTextNode(o.label));
    if (i === rec) lbl.appendChild(el("span", "rec-tag", "↹ recommended"));
    txt.appendChild(lbl);
    if (o.tradeoff) txt.appendChild(el("div", "otrade", o.tradeoff));
    row.appendChild(txt);
    const dot = el("span", "prefetch-dot" + (node.prefetch[i]?.status === "ready" || node.childId[i] ? " ready" : ""));
    dot.title = "prefetched — descent will be instant";
    row.appendChild(dot);
    row.onclick = () => void pick(i);
    row.addEventListener("mouseenter", () => queuePeek(node, i, row));
    row.addEventListener("mouseleave", hidePeek);
    optsEl.appendChild(row);
  });
  main.appendChild(optsEl);

  // replay review finding (if this decision has been validated)
  if (node.verdict && (node.verdict.note || node.verdict.better)) {
    const rv = el("div", "review " + (VERDICT_CLS[node.verdict.verdict] || "v-sound"));
    rv.appendChild(el("span", "rv-tag", node.verdict.verdict));
    if (node.verdict.note) rv.appendChild(el("span", "rv-note", node.verdict.note));
    if (node.verdict.better) {
      // if the suggestion names an actual option, one click accepts it
      const match = matchOptionLabel(node, node.verdict.better);
      if (match != null && match !== node.chosenIndex) {
        const btn = el("button", "rv-apply", `↷ switch to: ${node.options[match]!.label}`);
        btn.title = "Accept the reviewer's stronger option — re-picks it and updates the doc";
        btn.onclick = () => void applySuggestion(node.id, match);
        rv.appendChild(btn);
      } else {
        rv.appendChild(el("span", "rv-better", "consider: " + node.verdict.better));
      }
    }
    main.appendChild(rv);
  }

  // external reviewer annotations (attributed, per enabled layer)
  reviewNotesFor(node.id).forEach((a) => {
    const rv = el("div", "review ext " + (VERDICT_CLS[a.verdict || ""] || "v-sound"));
    rv.appendChild(el("span", "rv-author", a.author));
    rv.appendChild(el("span", "rv-tag", a.verdict || "note"));
    if (a.note) rv.appendChild(el("span", "rv-note", a.note));
    if (a.suggest) rv.appendChild(el("span", "rv-better", "suggests: " + a.suggest));
    main.appendChild(rv);
  });

  // alternative branches proposed by reviewers — a fork off this decision
  reviewBranchesFrom(node.id).forEach((b) => {
    const alt = el("div", "altbranch");
    const head = el("div", "alt-head");
    head.appendChild(el("span", "rv-author", b.author));
    head.appendChild(el("span", "alt-label",
      `⑂ alternative: take “${node.options[b.optionIndex]?.label || "another option"}” — ${b.label}`));
    head.appendChild(el("span", "alt-expand", "▸ show the line"));
    const chain = el("div", "alt-chain");
    (b.nodes || []).forEach((s) => {
      const row = el("div", "alt-step");
      row.appendChild(el("div", "alt-q", s.question));
      row.appendChild(el("div", "alt-take", "→ " + s.take));
      if (s.why) row.appendChild(el("div", "alt-why", s.why));
      chain.appendChild(row);
    });
    chain.style.display = "none";
    head.onclick = () => {
      const open = chain.style.display === "none";
      chain.style.display = open ? "" : "none";
      head.querySelector(".alt-expand")!.textContent = open ? "▾ hide" : "▸ show the line";
    };
    alt.appendChild(head);
    alt.appendChild(chain);
    main.appendChild(alt);
  });

  // reviewer overall summaries live on the root card
  if (node.depth === 0) {
    reviewSummaries().forEach((s) => {
      const rv = el("div", "review ext v-sound");
      rv.appendChild(el("span", "rv-author", s.author));
      rv.appendChild(el("span", "rv-note", s.text));
      main.appendChild(rv);
    });
  }

  // confidence
  const foot = el("div", "node-foot");
  const conf = el("div", "conf");
  conf.appendChild(document.createTextNode("confidence"));
  conf.appendChild(bar(node.confidence));
  conf.appendChild(el("span", null, Math.round(node.confidence * 100) + "%"));
  foot.appendChild(conf);
  main.appendChild(foot);

  // inject freetext
  const iw = el("div", "inject-wrap");
  const inj = el("div", "inject");
  inj.appendChild(el("span", "pfx", "fact ›"));
  const input = el("input") as HTMLInputElement;
  input.id = "injectInput";
  input.placeholder = "inject a fact the navigator missed  (e.g. records are ~1KB, 80% traffic us-east)…";
  input.autocomplete = "off";
  inj.appendChild(input);
  inj.appendChild(el("small", null, "↵ to inject · lowers confidence"));
  iw.appendChild(inj);
  main.appendChild(iw);
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") { const v = input.value; input.value = ""; void injectFact(v); }
    e.stopPropagation();
  });

  main.appendChild(buildGround());
}

// ---- grounding chips --------------------------------------------------------
function buildGround(): HTMLElement {
  const g = el("div", "ground");
  g.id = "groundBlock";
  fillGround(g);
  return g;
}

function fillGround(g: HTMLElement): void {
  g.innerHTML = "";
  const gt = el("div", "gt");
  gt.appendChild(document.createTextNode("grounding"));
  gt.appendChild(el("span", "sim", "SIMULATED"));
  g.appendChild(gt);
  const chips = el("div", "gchips");
  if (!state.grounding.length) {
    chips.appendChild(el("div", "gchip", "pulling production scalars…"));
  } else {
    state.grounding.forEach((m, idx) => {
      const c = el("div", "gchip");
      c.style.animationDelay = (idx * 40) + "ms";
      c.appendChild(el("span", "k", m.k + " "));
      c.appendChild(el("span", "v", m.v));
      chips.appendChild(c);
    });
  }
  state.facts.forEach((f) => {
    const c = el("div", "gchip injected");
    c.appendChild(el("span", "k", "injected "));
    c.appendChild(el("span", "v", truncate(f, 44)));
    chips.appendChild(c);
  });
  g.appendChild(chips);
}

/**
 * When grounding lands, refill the chips in place (no full card rebuild → the
 * inject input keeps its focus/text).
 */
export function refreshGroundingChips(): void {
  const g = document.getElementById("groundBlock");
  if (g) fillGround(g);
}

export function markPrefetchReady(i: number): void {
  const row = document.querySelector(`.option[data-i="${i}"] .prefetch-dot`);
  if (row) row.classList.add("ready");
}

export function highlightOption(i: number): void {
  document.querySelectorAll(".option").forEach((o) => o.classList.remove("sel"));
  const row = document.querySelector(`.option[data-i="${i}"]`);
  if (row) row.classList.add("sel");
}

// ---- peek: hover an option to preview the already-computed next node -------
// Costs nothing — the child was speculatively generated the moment this node
// rendered. Seeing the future before you commit IS the demo.
let peekTimer: ReturnType<typeof setTimeout> | null = null;

function queuePeek(node: DecisionNode, i: number, row: HTMLElement): void {
  hidePeek();
  peekTimer = setTimeout(() => {
    const staged = node.childId[i] ? state.nodes.get(node.childId[i]!)
      : node.prefetch[i]?.status === "ready" ? node.prefetch[i]!.node : null;
    if (!staged) return;
    const pk = el("div", "peek");
    pk.appendChild(el("div", "pk-tag", "⚡ next, already computed"));
    pk.appendChild(el("div", "pk-q", staged.question));
    staged.options.slice(0, 4).forEach((o, j) =>
      pk.appendChild(el("div", "pk-opt", `${j + 1}. ${o.label}`)));
    row.appendChild(pk);
  }, 220);
}

function hidePeek(): void {
  if (peekTimer) clearTimeout(peekTimer);
  document.querySelector(".peek")?.remove();
}
